import { CohortType } from '@dynamika-shared/types/cohorts';
import { TableNew, TableRecord } from '@dynamika-shared/ui/TableNew/TableNew';
import { Baseline, EditSmallIcon, TrashIcon } from '@dynamika-shared/ui/icons';
import { ReactNode } from 'react';
import { Tooltip } from 'antd';
import { MAX_LENGTH_NAME } from 'src/pages/ClinicalTrial/ClinicalTrialVisits/CohortList';
import { VisitsControlSchemaType } from 'src/pages/ClinicalTrial/uiControlSchemaByStatus/ControlTypes';
import { VisitConfig } from '@dynamika-shared/types/visits';
import { twMerge } from '@utils/twMerge';

export type VisitDataSourceItem = {
  key: string;
  name: ReactNode;
  baseline: ReactNode;
  action: ReactNode;
  disabled?: boolean;
};

const expandedColumns = (disabled?: boolean, isPM: boolean = false) => [
  {
    title: 'Name',
    dataIndex: 'name',
    className: 'w-[20%] min-w-[20%] max-w-[20%]',
  },
  { title: 'Days from Baseline', dataIndex: 'baseline' },
  {
    title: (
      <div className={twMerge('text-right', disabled && 'text-platinum')}>
        {!isPM && (<span>Action</span>)}
      </div>
    ),
    dataIndex: 'action',
    key: 'action',
  },
];

export const transformExpandedRows =
  (
    uiControlSchema: VisitsControlSchemaType,
    editVisitAction: (visit: VisitConfig, cohort: CohortType) => void,
    deleteVisitAction: (visitId: string) => void,
    isPM: boolean = false
  ) =>
  (cohort: CohortType) => {
    const memoizedVisitsDataSource: VisitDataSourceItem[] = [...cohort.visits]
      .sort((a, b) => a.daysFromBaseline - b.daysFromBaseline)
      .map((visit, i) => ({
        key: `${visit.id}-${i}`,
        name: (
          <Tooltip
            color='white'
            className='flex items-center gap-1'
            title={
              visit.name.length > MAX_LENGTH_NAME && (
                <span className={'text-black'}>{visit.name}</span>
              )
            }
          >
            <div className='max-w-full truncate'>{visit.name}</div>
            {!visit.daysFromBaseline && (
              <Baseline className='text-pastelBlue' />
            )}
          </Tooltip>
        ),
        baseline: <div>{visit.daysFromBaseline} days</div>,
        action: (
          <div className='flex justify-end gap-1'>
            {!uiControlSchema.editVisit.hidden && !isPM && (
              <EditSmallIcon
                className='cursor-pointer hover:text-dodgerBlue'
                width={24}
                height={24}
                onClick={() => editVisitAction(visit, cohort)}
                data-tesitid={`editSpecVisit-${visit.id}`}
              />
            )}
            {!uiControlSchema.deleteVisit.hidden && !isPM && (
              <TrashIcon
                className='cursor-pointer hover:text-dodgerBlue'
                width={24}
                height={24}
                onClick={() => deleteVisitAction(visit.id)}
                data-tesitid={`deleteSpecVisit-${visit.id}`}
              />
            )}
          </div>
        ),
      }));
    return (
      <TableNew<TableRecord>
        columns={expandedColumns(
          uiControlSchema.deleteVisit.hidden || uiControlSchema.editVisit.hidden,
          isPM
        )}
        dataSource={memoizedVisitsDataSource}
        pagination={false}
        tableClassName='nested-table !bg-white'
      />
    );
  };
